// cspell:ignore Chromebox, olea, Instore, DISTY
import { KioskDeviceType, KioskOs } from '@biteinc/enums';
import { HardwareConfiguration, HardwareManufacturer, HardwareOrientation, HardwarePeripheral, ProductComponentType, Tier, } from './schema.js';
const directPurchaseMethod = 'Direct Purchase: ResourcePOS, Spencer, RDS, Stingray';
const eloPurchaseMethod = `
${directPurchaseMethod}

HaaS: Spencer
`;
const microTouchPurchaseMethod = `
${directPurchaseMethod}
To access this price, Integrator (e.g. Resource / Stingray / Spencer etc.) must specify to DISTY they are on Bite blanket pricing. DISTYs notified are: Scansource, Bluestar, Almo Exertis, Ingram.

Item\t\t\t\t\t\t\t\t\tPart Number\t\t\tDISTY\t\tExpected
\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\t\tPrice\t\tCustomer Price
----------------------------------------------------------------------
ChromeOS 21.5" Intel\t\tM1-215IC-NC-A2\t$720.00\t$882.00
Android 21.5"\t\t\t\t\tM1-215IC-AA2\t\t$514.00\t$629.65
Scanner\t\t\t\t\t\t\tMA-SCAN-A1
`;
const samsungPurchaseMethod = `
${directPurchaseMethod}

HaaS: Spencer
`;
// Works on all Elo devices (Android or ChromeOS)
const eloScanners = `Elo E926356 (Honeywell N3600 barcode engine)
Elo E393160 (Zebra SE2707 barcode engine)
Elo E384627 (Zebra SE4720 barcode engine)`;
const zebraDs457 = 'Zebra DS457'; // Android / ChromeOS / iOS
const zebraDs9308 = 'Zebra DS9308'; // Android / ChromeOS
const zebraSe4757 = 'Zebra SE4757'; // Samsung ChromeOS
const honeywellN33x0 = 'Honeywell N3310/N3320'; // Android / ChromeOS
const honeywellCm56080Sr = 'Honeywell CM5680SR'; // ChromeOS
const newlandFm3280 = 'Newland FM3280-R-U-EC00378 (Frank Mayer; USB CDC; certification pending)';
const newlandFm3280Note = {
    title: 'Frank Mayer scanner',
    description: 'Newland FM3280 is built into the Frank Mayer kiosk enclosure. Requires USB CDC mode and a Bite native build with Newland support. Android was tested on an Elo test host. ChromeOS and full kiosk certification remain pending.',
};
const eloWebcamNote = {
    title: 'External Cameras',
    description: 'The built-in camera on the Elo is located on the side, so it is not very effective for facial recognition. Instead, a separate Elo camera should be purchased: E210494 (https://www.elotouch.com/accessories-webcam.html)',
};
const iOSEolNote = {
    title: 'EOL Notice (2024-09-24)',
    description: 'The iOS app has reached its EOL. Existing customers are still using it, but we are not looking to update it to use Vitrine due to high costs.\n\nShould there be a suitably big client, it can, however, be revived.',
};
const tizenEolNote = {
    title: 'EOL Notice (2025-02-07)',
    description: 'The Tizen app has reached its EOL. Existing customers are still using it, but we are not looking to update it to use Vitrine due to high costs and hardware limitations.\n\nIf a customer wants to use Samsung Hardware they should use ChromeOS Samsung KM24C/W or ChromeOS Samsung KM24A Tizen Convert instead.',
};
function noAvailabilityForUsbDevices(manufacturer) {
    return {
        availability: 'never',
        explanation: `USB peripherals cannot be connected to ${manufacturer} tablets`,
    };
}
export const hardwareProductComponents = [
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Elo 15"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-elo-15@2x.png',
        manufacturer: HardwareManufacturer.Elo,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidElo15,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [eloScanners, zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'E390075 / E391032',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.Gold,
        purchaseMethod: eloPurchaseMethod,
        notes: [eloWebcamNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Elo 22"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-elo-22@2x.png',
        manufacturer: HardwareManufacturer.Elo,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidElo22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [eloScanners, zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'E390263 / E391414',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.Gold,
        purchaseMethod: eloPurchaseMethod,
        notes: [eloWebcamNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Elo Backpack\n(22", 27", 32")',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-elo-android-backpack@2x.png',
        manufacturer: HardwareManufacturer.Elo,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidElo22Backpack,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [eloScanners, zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'E3993359',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.Gold,
        purchaseMethod: eloPurchaseMethod,
        notes: [eloWebcamNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Lenovo K10',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-lenovo-android@2x.png',
        manufacturer: HardwareManufacturer.Lenovo,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidLenovoK10,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('Lenovo'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('Lenovo'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('Lenovo'),
            [HardwarePeripheral.Scanner]: noAvailabilityForUsbDevices('Lenovo'),
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Only supports a Network Printer'],
            },
            [HardwarePeripheral.StormAudioNav]: noAvailabilityForUsbDevices('Lenovo'),
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'None, because USB devices cannot be plugged in.',
        partNumber: 'TB-X6C6L',
        pricePoint: '$',
        leadTime: [1, 2],
        tier: Tier.Bronze,
        purchaseMethod: 'TODO',
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Flytech + Acrelec',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-flytech-android-acrelec@2x.png',
        manufacturer: HardwareManufacturer.Flytech,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidFlytechAcrelec,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: true,
                notes: ['TODO: Confirm whether standard external printers are also supported.'],
            },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: true },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'Built-in scanner. TODO: Confirm the model and part number.',
        partNumber: 'Flytech: TODO; Acrelec: K22 / K27 (part numbers TODO)',
        pricePoint: '$$$$',
        leadTime: [8, 10],
        tier: Tier.Bronze,
        purchaseMethod: 'Glory; talk to Jeff / Kristy.',
        notes: [
            {
                title: 'TODO: Product Details Pending',
                description: 'Confirm the exact Flytech computer model and part number, the Acrelec K22 and K27 part numbers, whether Admin should use “Android Flytech / Acrelec” or “Android Flytech + Acrelec,” and the exact lead time in weeks. Jeff described the lead time as very long.',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Samsung Galaxy Tab A Series',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-samsung-android@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidSamsungGalaxyTabASeries,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scanner]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Only supports a network printer'],
            },
            [HardwarePeripheral.StormAudioNav]: noAvailabilityForUsbDevices('Samsung'),
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'None, because USB devices cannot be plugged in.',
        partNumber: 'SM-X210 / SM-X215 / SM-X216B',
        pricePoint: '$',
        leadTime: [1, 2],
        tier: Tier.Bronze,
        purchaseMethod: 'TODO',
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Samsung Galaxy Tab S Series',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-samsung-android@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidSamsungGalaxyTabSSeries,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scanner]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Only supports a network printer'],
            },
            [HardwarePeripheral.StormAudioNav]: noAvailabilityForUsbDevices('Samsung'),
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'None, because USB devices cannot be plugged in.',
        partNumber: 'SM-T7?? / SM-T8?? / SM-T9?? (Galaxy Tab S7), SM-X7?? / SM-X8?? / SM-X9?? (Galaxy Tab S8/S9)',
        pricePoint: '$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: 'TODO',
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Samsung Galaxy Tab Active Pro Series',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-samsung-android@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidSamsungGalaxyTabActiveProSeries,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.Scanner]: noAvailabilityForUsbDevices('Samsung'),
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Only supports a network printer'],
            },
            [HardwarePeripheral.StormAudioNav]: noAvailabilityForUsbDevices('Samsung'),
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'None, because USB devices cannot be plugged in.',
        partNumber: 'SM-T540 / SM-T547 (Active Pro 2019), SM-T636 (Active 4 Pro)',
        pricePoint: '$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: 'TODO',
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Acrelec K Series',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-acrelec-chromeos@2x.png',
        manufacturer: HardwareManufacturer.Acrelec,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsAcrelecK22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'Built-in Only',
        partNumber: 'K22, K27, K27 Lite',
        pricePoint: '$$$$',
        leadTime: [3, 3],
        tier: Tier.Silver,
        purchaseMethod: directPurchaseMethod,
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Elo\n22" / 24"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-elo-chromeos-22@2x.png',
        manufacturer: HardwareManufacturer.Elo,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsElo22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [eloScanners, zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'E692448 (no OS) / E291723 (ChromeOS) / lots of other Windows or No OS models Elo has',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.Bronze,
        purchaseMethod: eloPurchaseMethod,
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS\nHP EngagePro\n19.5" / 24"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-hp-chromeos@2x.png',
        manufacturer: HardwareManufacturer.HP,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsHPEngagePro24,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                notes: ['Printer is built-into the freestanding unit.'],
                isBuiltIn: false,
            },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'Built-in Only',
        partNumber: 'TODO',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.Silver,
        purchaseMethod: eloPurchaseMethod,
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Lenovo Chromebox Micro + Instore',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-lenovo-chromeos-micro-instore@2x.png',
        manufacturer: HardwareManufacturer.Lenovo,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsLenovoChromeBox,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: true,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: '83F90000US (Chromebox) + 40EMTD9017 (InstoreScreen)',
        pricePoint: '$$$',
        leadTime: [8, 10],
        tier: Tier.Gold,
        purchaseMethod: directPurchaseMethod,
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Lenovo Chromebox Micro + Olea',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-lenovo-chromeos-micro-olea@2x.png',
        manufacturer: HardwareManufacturer.Lenovo,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsLenovoChromeBox,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: true,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0, honeywellCm56080Sr].join('\n'),
        partNumber: '83F90000US + Olea',
        pricePoint: '$$$',
        leadTime: [8, 10],
        tier: Tier.Bronze,
        purchaseMethod: 'Olea',
        notes: [
            {
                title: `Honeywell CM5680SR Scanner Note`,
                description: 'For use in custom enclosures only',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'iPad Pro 10.9"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-ios-10@2x.png',
        manufacturer: HardwareManufacturer.Apple,
        os: KioskOs.iOS,
        deviceType: KioskDeviceType.iPad,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'yes',
                isBuiltIn: true,
            },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Requires an Epson printer'],
            },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: `iOS has a built-in accessibility mode`,
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: `${zebraDs457} (requires an Epson printer)`,
        partNumber: 'iPad 10G / iPad Air / iPad Pro 11',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.EOL,
        purchaseMethod: 'Direct: ResourcePOS',
        notes: [iOSEolNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'iPad Pro 12.9"',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-ios-13@2x.png',
        manufacturer: HardwareManufacturer.Apple,
        os: KioskOs.iOS,
        deviceType: KioskDeviceType.iPadPro,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'yes',
                isBuiltIn: true,
            },
            [HardwarePeripheral.HID]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.MSR]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.Scale]: noAvailabilityForUsbDevices('iOS'),
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: false,
                notes: ['Requires an Epson printer'],
            },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: `iOS has a built-in accessibility mode`,
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: true,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: `${zebraDs457} (requires an Epson printer)`,
        partNumber: 'iPad Pro 12.9"',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.EOL,
        purchaseMethod: 'Direct: ResourcePOS',
        notes: [iOSEolNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Samsung KM24A Tizen Convert',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-tizen-convert-chromeos@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsSamsungKM24ALenovoChromeBox,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it; no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.MSR]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scale]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: true,
                notes: [
                    `If using ${zebraSe4757}, the scanner needs to be set to presentation mode & USB CDC mode with 123Scan Windows Utility`,
                    `If using ${zebraSe4757}, must install the scanner in kiosk scanner slot`,
                ],
            },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: false,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: ['Built-in', zebraSe4757].join('\n'),
        partNumber: 'Samsung KM24A + Lenovo 83F90000US',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.Gold,
        purchaseMethod: 'Buy a KM24A from resource, ask them to also stage and setup a Chromebox',
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Samsung KM24A Flytech',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-flytech-android-acrelec@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidSamsungKM24AFlytech,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.HID]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.MSR]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scale]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: true,
            },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: false,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'Built-in',
        // TODO: add the Flytech box's own part number alongside the KM24A once ops confirms it.
        partNumber: 'Samsung KM24A + Flytech Android box',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: 'TODO',
        notes: [
            {
                title: 'Takeover use only',
                description: 'Only to be used for takeovers, when FreedomPay is required.',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Tizen Samsung KM24A',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-samsung-tizen@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.Tizen,
        deviceType: KioskDeviceType.TizenSamsungKM24A,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'never',
                explanation: 'Tizen has no support for it',
            },
            [HardwarePeripheral.HID]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.MSR]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scale]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: 'Tizen has no support for it',
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: false,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: 'Built-in Only',
        partNumber: 'Samsung KM24A',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.EOL,
        purchaseMethod: samsungPurchaseMethod,
        notes: [tizenEolNote],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Samsung KM24C/W',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-samsung-chromeos@2x.png',
        manufacturer: HardwareManufacturer.Samsung,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsSamsungKM24W,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it; no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.MSR]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scale]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: true,
                notes: [
                    `If using ${zebraSe4757}, the scanner needs to be set to presentation mode & USB CDC mode with 123Scan Windows Utility`,
                    `If using ${zebraSe4757}, must install the scanner in kiosk scanner slot`,
                ],
            },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: true },
            [HardwarePeripheral.StormAudioNav]: {
                availability: 'never',
                explanation: 'no way to plug it in, or mount it',
            },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: false,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: ['Built-in', zebraSe4757].join('\n'),
        partNumber: 'Samsung KM24C / KM24W',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: samsungPurchaseMethod,
        notes: [],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android MicroTouch',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-micro-touch-android@2x.png',
        manufacturer: HardwareManufacturer.MicroTouch,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidMicroTouch22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'need based',
            },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0, newlandFm3280].join('\n'),
        partNumber: 'Mach',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.Gold,
        purchaseMethod: microTouchPurchaseMethod,
        notes: [
            newlandFm3280Note,
            {
                title: 'Stands',
                description: 'Requires a separate stand (Olea, Space Pole, Frank Mayer)',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS MicroTouch',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-micro-touch-chromeos@2x.png',
        manufacturer: HardwareManufacturer.MicroTouch,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsMicroTouch22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0, newlandFm3280].join('\n'),
        partNumber: 'Mach',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.Silver,
        purchaseMethod: microTouchPurchaseMethod,
        notes: [
            newlandFm3280Note,
            {
                title: 'Stands',
                description: 'Requires a separate stand (Olea, Space Pole, Frank Mayer)',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Touch Dynamic',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-touch-dynamic-android@2x.png',
        manufacturer: HardwareManufacturer.TouchDynamic,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidTouchDynamic,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'need based',
            },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0, newlandFm3280].join('\n'),
        partNumber: 'Edge, Edge Ultra',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: directPurchaseMethod,
        notes: [
            newlandFm3280Note,
            {
                title: 'Stands',
                description: 'Touch Dynamic Edge Stand (counter / free-standing) or a can work with a separate stand from Olea, Space Pole, Frank Mayer',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Touch Dynamic',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-touch-dynamic-chromeos@2x.png',
        manufacturer: HardwareManufacturer.TouchDynamic,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsTouchDynamicEdgeUltra22,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0, newlandFm3280].join('\n'),
        partNumber: 'Edge, Edge Ultra',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.Bronze,
        purchaseMethod: directPurchaseMethod,
        notes: [
            newlandFm3280Note,
            {
                title: 'Stands',
                description: 'Touch Dynamic Edge Stand (counter / free-standing) or a can work with a separate stand from Olea, Space Pole, Frank Mayer',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'Android Posiflex',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-posiflex-android@2x.png',
        manufacturer: HardwareManufacturer.Posiflex,
        os: KioskOs.Android,
        deviceType: KioskDeviceType.AndroidPosiflexMercury,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'need based',
            },
            [HardwarePeripheral.HID]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'JK Series, EK 21, 24, TK (Paragon)',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: 'Direct Purchase: Posiflex',
        notes: [
            {
                title: 'Stands',
                description: 'JK, EK and TK have freestanding or counter configurations',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS Posiflex',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-posiflex-chromeos@2x.png',
        manufacturer: HardwareManufacturer.Posiflex,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsPosiflexMercury,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.ReceiptPrinter]: { availability: 'yes', isBuiltIn: false },
            [HardwarePeripheral.StormAudioNav]: { availability: 'yes', isBuiltIn: false },
        },
        configurations: {
            [HardwareConfiguration.Counter]: true,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: true,
        },
        orientations: {
            [HardwareOrientation.Portrait]: true,
            [HardwareOrientation.Landscape]: false,
        },
        canBeUsedOutdoors: false,
        availableScanners: [zebraDs457, zebraDs9308, honeywellN33x0].join('\n'),
        partNumber: 'JK Series, EK 21, 24, TK (Paragon)',
        pricePoint: '$$',
        leadTime: [1, 2],
        tier: Tier.Bronze,
        purchaseMethod: 'Direct Purchase: Posiflex',
        notes: [
            {
                title: 'Stands',
                description: 'JK, EK and TK have freestanding or counter configurations',
            },
        ],
    },
    {
        componentType: ProductComponentType.Hardware,
        name: 'ChromeOS NCR 7360',
        iconUrl: 'https://static.bureau.getbite.com/images/status-kiosk-chrome@2x.png',
        manufacturer: HardwareManufacturer.NCR,
        os: KioskOs.ChromeOS,
        deviceType: KioskDeviceType.ChromeOsNcr7360,
        availabilityByPeripheral: {
            [HardwarePeripheral.Camera]: {
                availability: 'coming',
                expectedDate: 'no ETA; blocked on Google',
            },
            [HardwarePeripheral.HID]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.MSR]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scale]: { availability: 'coming', expectedDate: 'need based' },
            [HardwarePeripheral.Scanner]: {
                availability: 'yes',
                isBuiltIn: true,
                notes: [
                    'The built-in Zebra scanner ships in IBM/OPOS host mode. Scan the Zebra "USB CDC Host" programming barcode so it enumerates as a USB CDC scanner.',
                ],
            },
            [HardwarePeripheral.ReceiptPrinter]: {
                availability: 'yes',
                isBuiltIn: true,
                notes: [
                    'The built-in NCR NHPI receipt printer uses WebHID on ChromeOS. Printing, cutting, and setup were tested on the NCR unit.',
                ],
            },
            [HardwarePeripheral.StormAudioNav]: { availability: 'coming', expectedDate: 'need based' },
        },
        configurations: {
            [HardwareConfiguration.Counter]: false,
            [HardwareConfiguration.FreeStanding]: true,
            [HardwareConfiguration.Linebuster]: false,
            [HardwareConfiguration.WallMounted]: false,
        },
        orientations: {
            [HardwareOrientation.Portrait]: false,
            [HardwareOrientation.Landscape]: true,
        },
        canBeUsedOutdoors: false,
        availableScanners: ['Built-in', zebraDs457, zebraDs9308].join('\n'),
        partNumber: '7360-4010-2000',
        pricePoint: '$$$',
        leadTime: [1, 2],
        tier: Tier.LabTested,
        purchaseMethod: 'TODO',
        notes: [
            {
                title: 'Built-in receipt printer',
                description: 'NCR NHPI USB printer. ChromeOS uses WebHID. Receipt printing, cutting, and setup were tested on the NCR unit.',
            },
            {
                title: 'TODO: Product Details Pending',
                description: "Provisional entry for the Love's Express Checkout demo lab unit. Price point, lead time, purchase method, mounting configurations, and support tier are assumed, not confirmed by ops.",
            },
            {
                title: 'Display',
                description: '1024x768 landscape (4:3) panel that cannot be rotated.',
            },
        ],
    },
];
//# sourceMappingURL=hardware.js.map